import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileText,
  Download,
  File,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reports({ user, logout, token }) {
  const [reports, setReports] = useState([]);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedScan, setSelectedScan] = useState("");
  const [reportType, setReportType] = useState("executive");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [reportsRes, scansRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reports`, { headers, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/scans`, { headers, withCredentials: true })
      ]);
      setReports(reportsRes.data);
      setScans(scansRes.data.filter(s => s.status === 'completed'));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedScan) {
      toast.error("Please select a scan first");
      return;
    }

    setGenerating(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${BACKEND_URL}/api/reports`, {
        scan_id: selectedScan,
        report_type: reportType
      }, { headers, withCredentials: true });
      
      toast.success("Report generated successfully!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (reportId, format) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${BACKEND_URL}/api/reports/${reportId}/download/${format}`,
        { headers, withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${reportId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} downloaded successfully!`);
    } catch (error) {
      toast.error("Failed to download report");
    }
  };

  const getReportTypeBadge = (type) => {
    const variants = {
      executive: "bg-blue-500/10 text-blue-500",
      technical: "bg-purple-500/10 text-purple-500",
      compliance: "bg-emerald-500/10 text-emerald-500"
    };
    return variants[type] || variants.executive;
  };

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-primary">Loading reports...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold font-[Chivo]">Reports</h2>
          <p className="text-muted-foreground">
            Generate and download security assessment reports
          </p>
        </div>

        {/* Generate Report */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-[Chivo]">Generate New Report</CardTitle>
            <CardDescription>
              Create professional PDF and editable DOCX reports from your scans
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Select value={selectedScan} onValueChange={setSelectedScan}>
                  <SelectTrigger data-testid="select-scan">
                    <SelectValue placeholder="Select a completed scan" />
                  </SelectTrigger>
                  <SelectContent>
                    {scans.length === 0 ? (
                      <SelectItem value="none" disabled>No completed scans available</SelectItem>
                    ) : (
                      scans.map((scan) => (
                        <SelectItem key={scan.scan_id} value={scan.scan_id}>
                          {scan.name} - {new Date(scan.created_at).toLocaleDateString()}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-full md:w-48">
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger data-testid="select-report-type">
                    <SelectValue placeholder="Report type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="executive">Executive Summary</SelectItem>
                    <SelectItem value="technical">Technical Report</SelectItem>
                    <SelectItem value="compliance">Compliance Report</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                onClick={handleGenerateReport}
                disabled={generating || !selectedScan}
                className="glow-primary"
                data-testid="generate-report-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Report
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Report List */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-[Chivo]">Generated Reports</CardTitle>
            <CardDescription>
              Download your previously generated reports
            </CardDescription>
          </CardHeader>
          <CardContent>
            {reports.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No reports yet</h3>
                <p className="text-muted-foreground">
                  Generate your first report from a completed scan
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {reports.map((report, index) => {
                  const scan = scans.find(s => s.scan_id === report.scan_id);
                  
                  return (
                    <motion.div
                      key={report.report_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center justify-between p-4 border border-border rounded-sm hover:border-primary/30 transition-colors"
                      data-testid={`report-${report.report_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-sm bg-primary/10 flex items-center justify-center">
                          <FileText className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{scan?.name || 'Unknown Scan'}</p>
                            <Badge className={getReportTypeBadge(report.report_type)}>
                              {report.report_type}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span>{new Date(report.created_at).toLocaleString()}</span>
                            {report.status === 'ready' ? (
                              <CheckCircle className="h-3 w-3 text-emerald-500" />
                            ) : (
                              <AlertCircle className="h-3 w-3 text-yellow-500" />
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(report.report_id, 'pdf')}
                          disabled={report.status !== 'ready'}
                          data-testid={`download-pdf-${report.report_id}`}
                        >
                          <File className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(report.report_id, 'docx')}
                          disabled={report.status !== 'ready'}
                          data-testid={`download-docx-${report.report_id}`}
                        >
                          <File className="h-4 w-4 mr-1" />
                          DOCX
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Report Types Info */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-sm bg-blue-500/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-500" />
                </div>
                <h3 className="font-medium">Executive Summary</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                High-level overview with risk scores, key findings, and recommendations 
                suitable for leadership and board presentations.
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-sm bg-purple-500/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-purple-500" />
                </div>
                <h3 className="font-medium">Technical Report</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Detailed vulnerability findings with technical descriptions, affected 
                components, and step-by-step remediation guidance.
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-sm bg-emerald-500/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-emerald-500" />
                </div>
                <h3 className="font-medium">Compliance Report</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Framework-specific compliance assessment with control mappings, 
                pass/fail status, and evidence documentation for auditors.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
